import { useState, useEffect } from 'react'

const SCRIPT_SAMPLES = [
  { name: 'German (Deutsch)', text: 'Müller, Straße, Zürich, ÄÖÜ ß, Größenordnung', lang: 'de' },
  { name: 'French (Français)', text: 'naïve, café, résumé, crème brûlée, François', lang: 'fr' },
  { name: 'Spanish (Español)', text: 'señor, año, corazón, Málaga, über', lang: 'es' },
  { name: 'Portuguese (Português)', text: 'ação, coração, São Paulo, avô, pôde', lang: 'pt' },
  { name: 'Italian (Italiano)', text: 'città, perché, più, virtù, è', lang: 'it' },
  { name: 'Dutch (Nederlands)', text: 'maïs, coördinatie, één, vóór, geërfd', lang: 'nl' },
  { name: 'Polish (Polski)', text: 'cześć, łódź, Wrocław, żółć, ąćęłńóśźż', lang: 'pl' },
  { name: 'Czech (Čeština)', text: 'český, řeka, žluťoučký, háčky, čárky', lang: 'cs' },
  { name: 'Swedish (Svenska)', text: 'fjärran, skål, äta, överbrygga', lang: 'sv' },
  { name: 'Turkish (Türkçe)', text: 'merhaba, teşekkür, İstanbul, şçöğüı', lang: 'tr' },
  { name: 'Cyrillic (Русский)', text: 'Привет мир, Москва, Здравствуйте, русский язык', lang: 'ru' },
  { name: 'Ukrainian (Українська)', text: 'Привіт, Київ, Україна, ласкаво просимо', lang: 'uk' },
  { name: 'Greek (Ελληνικά)', text: 'Γεια σας κόσμο, Αθήνα, ελληνικά', lang: 'el' },
  { name: 'Arabic (العربية)', text: 'مرحبا بالعالم, القاهرة, العربية', lang: 'ar' },
  { name: 'Hebrew (עברית)', text: 'שלום עולם, ירושלים, עברית', lang: 'he' },
  { name: 'Persian/Farsi (فارسی)', text: 'سلام دنیا, تهران, فارسی', lang: 'fa' },
  { name: 'Hindi (हिन्दी)', text: 'नमस्ते दुनिया, नई दिल्ली, हिन्दी', lang: 'hi' },
  { name: 'Bengali (বাংলা)', text: 'নমস্কার, ঢাকা, বাংলা', lang: 'bn' },
  { name: 'Thai (ไทย)', text: 'สวัสดีชาวโลก, กรุงเทพฯ, ไทย', lang: 'th' },
  { name: 'Vietnamese (Tiếng Việt)', text: 'Xin chào thế giới, Hà Nội, tiếng Việt', lang: 'vi' },
  { name: 'Chinese Simplified (中文简体)', text: '你好世界，北京，中文', lang: 'zh-CN' },
  { name: 'Chinese Traditional (中文繁體)', text: '你好世界，北京，中文', lang: 'zh-TW' },
  { name: 'Japanese (日本語)', text: 'こんにちは世界、東京、日本語', lang: 'ja' },
  { name: 'Korean (한국어)', text: '안녕하세요 세계, 서울, 한국어', lang: 'ko' },
  { name: 'Emoji', text: '😀🎉🔥👍❤️🌍🚀💡✅⭐🎨📱', lang: 'und' },
]

function detectMissingGlyphs(text: string): string[] {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return []

  const testChars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  ctx.font = '16px sans-serif'
  const baselineWidth = ctx.measureText(testChars).width

  const missing: string[] = []
  for (const char of [...text]) {
    if (char.trim() === '' || char.charCodeAt(0) < 128) continue
    const charWidth = ctx.measureText(char).width
    if (charWidth === 0 || charWidth === baselineWidth / testChars.length) {
      missing.push(char)
    }
  }
  return missing
}

export function UnicodeTestPage() {
  const [fontSize, setFontSize] = useState(16)
  const [showCodepoints, setShowCodepoints] = useState(false)
  const [showMissingGlyphs, setShowMissingGlyphs] = useState(false)
  const [missingGlyphs, setMissingGlyphs] = useState<Record<string, string[]>>({})

  useEffect(() => {
    if (!showMissingGlyphs) return
    const results: Record<string, string[]> = {}
    for (const sample of SCRIPT_SAMPLES) {
      results[sample.lang] = detectMissingGlyphs(sample.text)
    }
    setMissingGlyphs(results)
  }, [showMissingGlyphs, fontSize])

  const getCodepoints = (text: string) => {
    return [...text].map(c => `U+${c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')}`).join(' ')
  }

  const totalMissing = Object.values(missingGlyphs).reduce((sum, arr) => sum + arr.length, 0)

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Unicode Character Test Page</h1>
          <p className="text-gray-600 mt-2">Verify rendering of international characters across all supported scripts.</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Font Size:</label>
            <input
              type="range"
              min="12"
              max="48"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-32"
            />
            <span className="text-sm text-gray-600 w-8">{fontSize}px</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="show-codepoints"
              checked={showCodepoints}
              onChange={(e) => setShowCodepoints(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="show-codepoints" className="text-sm text-gray-700">Show codepoints</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="show-missing"
              checked={showMissingGlyphs}
              onChange={(e) => setShowMissingGlyphs(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="show-missing" className="text-sm text-gray-700">
              Detect missing glyphs {totalMissing > 0 && <span className="text-red-600 font-bold">({totalMissing} missing)</span>}
            </label>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">Active fonts:</span>
            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
              Inter, Noto Sans, system-ui
            </code>
          </div>
        </div>

        <div className="grid gap-4">
          {SCRIPT_SAMPLES.map((sample) => {
            const missing = missingGlyphs[sample.lang] || []
            return (
              <div key={sample.lang} className={`bg-white rounded-lg shadow p-4 ${missing.length > 0 ? 'border-l-4 border-red-500' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900">{sample.name}</h2>
                  <div className="flex items-center gap-2">
                    {missing.length > 0 && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-mono">
                        {missing.length} missing
                      </span>
                    )}
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono">{sample.lang}</span>
                  </div>
                </div>
                <p style={{ fontSize: `${fontSize}px` }} className="text-gray-800 leading-relaxed">
                  {sample.text}
                </p>
                {showMissingGlyphs && missing.length > 0 && (
                  <p className="text-xs text-red-600 mt-2 font-mono">
                    Missing: {missing.join(' ')}
                  </p>
                )}
                {showCodepoints && (
                  <p className="text-xs text-gray-500 mt-2 font-mono break-all">
                    {getCodepoints(sample.text)}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        <div className="mt-8 bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Edge Cases</h2>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-700">Combining characters: </span>
              <span style={{ fontSize: `${fontSize}px` }}>é = e + ◌́ → é (precomposed) vs é (decomposed)</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">RTL mixed with LTR: </span>
              <span style={{ fontSize: `${fontSize}px` }} dir="auto">Hello مرحبا World שלום Test</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Zero-width joiner: </span>
              <span style={{ fontSize: `${fontSize}px` }}>👨‍👩‍👧‍👦 👨‍💻 🏳️‍🌈</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Mathematical symbols: </span>
              <span style={{ fontSize: `${fontSize}px` }}>∑ ∫ ∂ √ ∞ ≈ ≠ ≤ ≥ ∀ ∃ ∈ ∉</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Currency symbols: </span>
              <span style={{ fontSize: `${fontSize}px` }}>$ € £ ¥ ₹ ₽ ₪ ฿ ₫ ₩</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Arabic-Indic digits: </span>
              <span style={{ fontSize: `${fontSize}px` }} dir="rtl">٠١٢٣٤٥٦٧٨٩</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Thai digits: </span>
              <span style={{ fontSize: `${fontSize}px` }}>๐๑๒๓๔๕๖๗๘๙</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UnicodeTestPage
